import { DataSource } from "./base"
import { DataSourcePagination } from "../types"
import {
  TrendingEntry,
  TrendingFcEntry,
  TrendingFcResp,
  TrendingResp,
} from "@/types/farcaster"
import {
  getYourSubscriptions,
  getFarcasterFollowerPosts,
  getTrendingPosts,
} from "@/lib/api/discover"

/**
 * We want to do a very large page fetch.
 *
 * This is because we want to be able to merge together a bunch of different data sources to create the "For You" feed,
 * and it's extremely challenging to ensure proper ordering across multiple pages and multiple datasources.
 *
 * Also, the backend (for several datasources) just fetches all the data regardless then does a .slice() to paginate,
 * so it's not that much less performant to do this.
 */
const FETCH_PAGE_SIZE = 100
/**
 * After we do the large initial fetch above, we want to slice and return a smaller amount.
 *
 * The above data is cached via NextJS so it will be returned quickly.
 */
const RETURN_PAGE_SIZE = 10

/**
 * Creates the "For You" data.
 *
 * Fetches a bunch of different data sources and merges them together according to some heuristics.
 */
export default class ForYouDataSource implements DataSource {
  constructor(
    private userId?: string,
    private walletAddress?: string
  ) {}

  async fetchMore(pagination?: DataSourcePagination) {
    const page = pagination?.page || 1

    const promises = [
      this.userId
        ? getYourSubscriptions(this.userId, 1, FETCH_PAGE_SIZE)
        : Promise.resolve<TrendingResp>([]),
      this.walletAddress
        ? getFarcasterFollowerPosts(1, this.walletAddress, FETCH_PAGE_SIZE)
        : Promise.resolve<TrendingFcResp>([]),
      getTrendingPosts(undefined, undefined, FETCH_PAGE_SIZE),
      Promise.resolve<TrendingFcResp>([]),
      // getTrendingFarcasterPosts(1, undefined, FETCH_PAGE_SIZE),
    ]

    const [subscriptions, farcasterFollowers, trending, trendingFc] =
      await Promise.all(promises)

    console.log("Finished fetching ForYou data", {
      subscriptions: subscriptions.length,
      farcasterFollowers: farcasterFollowers.length,
      trending: trending.length,
      trendingFc: trendingFc.length,
    })

    const oldPage = pagination?.page || 1

    const result = this.mergePosts(
      subscriptions as TrendingResp,
      farcasterFollowers as TrendingFcResp,
      trending as TrendingResp,
      trendingFc as TrendingFcResp
    ).slice(RETURN_PAGE_SIZE * (page - 1), RETURN_PAGE_SIZE * page)

    const lastPost = result.findLast((post) =>
      trending.some((t) => t.post.id === post.post.id)
    )?.post

    const newPagination = {
      page: oldPage + 1,
      cursor: lastPost
        ? {
            publishedAt: Number(lastPost.publishedAt),
            id: lastPost.id,
          }
        : undefined,
    }

    return { data: result, pagination: newPagination }
  }

  getInitialPagination() {
    return { page: 1, cursor: undefined }
  }

  /**
   * Algorithm for merging together a bunch of datasources to create the "For You" feed.
   *
   * This does the following:
   *
   * 1. Orders all posts by reverse chronological order
   *   2. Adds posts from subscriptions first, then adds posts from farcaster followers, and finally adds trending posts (deduping all of them)
   *   3. If a single post is in multiple datasources (eg your subscriptions and FC followers), favor the ones that add more data (eg FC followers - we say "Published by your FC follower <name>" in the UI)
   *
   * */
  private mergePosts(
    subscriptions: TrendingResp,
    farcasterFollowers: TrendingFcResp,
    trending: TrendingResp,
    trendingFc: TrendingFcResp
  ): (TrendingEntry | TrendingFcEntry)[] {
    const subscriptionsMap = new Map<string, TrendingEntry>(
      subscriptions.map((post) => [post.post.id, post])
    )
    const farcasterFollowersMap = new Map<string, TrendingFcEntry>(
      farcasterFollowers.map((post) => [post.post.id, post])
    )
    const trendingMap = new Map<string, TrendingEntry>(
      trending.map((post) => [post.post.id, post])
    )

    const trendingFcMap = new Map<string, TrendingFcEntry>(
      trendingFc.map((post) => [post.post.id, post])
    )

    const result: (TrendingEntry | TrendingFcEntry)[] = []
    const seenIds = new Set<string>()

    const addIfNotSeen = (post: TrendingEntry | TrendingFcEntry) => {
      if (!seenIds.has(post.post.id)) {
        result.push(post)
        seenIds.add(post.post.id)
      }
    }

    // Process posts according to priority
    for (const [id, post] of subscriptionsMap) {
      if (farcasterFollowersMap.has(id)) {
        const farcasterPost = farcasterFollowersMap.get(id)
        if (farcasterPost) addIfNotSeen(farcasterPost)
      } else {
        addIfNotSeen(post)
      }
    }

    for (const post of farcasterFollowersMap.values()) {
      addIfNotSeen(post)
    }

    for (const post of trendingMap.values()) {
      addIfNotSeen(post)
    }

    for (const post of trendingFcMap.values()) {
      addIfNotSeen(post)
    }

    // Sort the result in reverse chronological order
    result.sort(
      (a, b) =>
        new Date(b.post.publishedAt || 0).getTime() -
        new Date(a.post.publishedAt || 0).getTime()
    )

    return result
  }
}
